<template>
  <div v-if="selectedProduct">
    <v-layout 
      v-for="(modifier) in positionByOrder(selectedProduct.displayedModifiers)" 
      :key="modifier.id" 
      column 
      mt-3 
      px-1
      :class="{'form-error': validateAnswers({ modifier, selectedOptions })}"
    >
      <div class="ma-2" v-if="modifier.options.length">
        <v-row>
          <v-col>
            <span class="primary--text text--darken-3">{{ modifier.callToAction }}</span>
            <span v-if="modifier.minSelected" class="red--text">*</span>
            <span v-if="modifier.minSelected > 1 || modifier.maxSelected > 1" class="show-spaces grey--text">  ({{
              [(modifier.minSelected > 1 ? $t('catalog.min_selected', { min: modifier.minSelected }) : ''),
                (modifier.maxSelected > 1 ? $t('catalog.max_selected', { max: modifier.maxSelected }) : '')].filter(s => s).join(', ')
            }})</span>
          </v-col>
          <v-col>
            <v-layout v-if="validateAnswers({ modifier, selectedOptions })" column align-end justify-end>
              <p class="caption error--text mb-0">{{ $t('activity_reservation.error.required_field') }}</p>
            </v-layout>
          </v-col>
        </v-row>
      </div>
      <modifier-radio-group 
        v-if="modifier.maxSelected === 1" 
        :modifier="modifier" 
        :getAmountAndCurrency="getAmountAndCurrency" 
      />
      <v-layout v-else column>
        <v-layout
          v-for="option in positionByOrder(modifier.options)" 
          :key="option.id"
          :set="amountAndCurrency = getAmountAndCurrency(option.id)"
          @click="handleClick({ option, modifier })"
          :class="{ 'clickable': !isCheckBoxDisabled({ option, modifier }) }"
          class="ma-2"
        >
          <v-row>
            <v-col>
              <v-checkbox
                :label="option.name" 
                color="primary" 
                class="mt-0 align-center" 
                hide-details
                :input-value="selectedOptions.includes(option.id)"
                :disabled="isCheckBoxDisabled({ option, modifier })"
                :ref="modifier.id"
                :rules="[() => rules.enoughSelected({ option, modifier, selectedOptions })]"
              />
            </v-col>
            <v-col cols="auto" >
              <v-layout v-if="amountAndCurrency" column align-end justify-center>
                <span class="primary--text">
                  {{ getPriceString({ amountAndCurrency: amountAndCurrency }) }}
                </span>
                <span v-if="showDisplayCurrency" label class="grey--text">
                  {{ getPriceString({ amountAndCurrency: amountAndCurrency, displayCurrency: displayCurrency }) }}
                </span>
              </v-layout>
            </v-col>
          </v-row>
        </v-layout>
      </v-layout>
    </v-layout>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex'

import { getPricingGroupWithPricesAppliedToAccount } from '@/lib/helpers/AccountUtils'
import { findOptionById } from '@/lib/helpers/CatalogUtils'

export default {
  props: {
    isValidForm: Boolean,
  },
  data () {
    return {
      rules: {
        enoughSelected: ({ modifier, option, selectedOptions }) => {
          return !this.stateProduct.quantity ||
            !modifier.minSelected ||
            selectedOptions.some((optionId) => optionId === option.id) ||
            selectedOptions.filter((optionId) => modifier.options.some(({ id }) => id === optionId)).length >= modifier.minSelected
        },
      },
    }
  },
  computed: {
    ...mapState({
      selectedProduct: ({ cart }) => cart.selectedProduct,
      products: ({ cart }) => cart.products,
    }),
    ...mapGetters('me', ['getPriceString', 'showDisplayCurrency', 'displayCurrency']),
    stateProduct () {
      return this.products.find(stateProduct => stateProduct.product.id === this.selectedProduct.id)
    },
    selectedOptions () {
      return this.stateProduct ? this.stateProduct.selectedOptions : []
    },
  },
  methods: {
    getAmountAndCurrency (optionId) {
      const option = findOptionById({ product: this.selectedProduct, optionId })
      const pricingGroupWithPrices = getPricingGroupWithPricesAppliedToAccount(option.price)
      return pricingGroupWithPrices
        ? pricingGroupWithPrices.prices[0][0].amount === 0
          ? false
          : pricingGroupWithPrices.prices[0][0]
        : null
    },
    isCheckBoxDisabled ({ option, modifier }) {
      return !!modifier.maxSelected &&
        this.selectedOptions.filter(optionId => modifier.options.some(({ id }) => id === optionId)).length === modifier.maxSelected &&
        !this.selectedOptions.includes(option.id)
    },
    handleClick ({ option, modifier }) {
      if (this.isCheckBoxDisabled({ option, modifier })) return
      this.$store.commit('cart/toggleSelectedOption', { optionId: option.id, productId: this.selectedProduct.id })
      this.$refs[modifier.id].forEach((checkBox) => {
        checkBox.resetValidation()
      })
    },
    positionByOrder (items) {
      function compare (a, b) {
        if (a.order < b.order)
          return -1
        if (a.order > b.order)
          return 1
        return 0
      }
      return items.slice().sort(compare)
    },
    validateAnswers ({ modifier, selectedOptions }) {
      const selectedOptionsLength = modifier.options.map((option) => 
        selectedOptions.some((optionSelected) => 
          option.id === optionSelected)).filter((opt) => opt).length

      return selectedOptionsLength < modifier.minSelected  && !this.isValidForm
    },
  },
  components: {
    ModifierRadioGroup: () => import('@/components/view-components/catalog/ModifierRadioGroup'),
  },
}
</script>

<style lang="scss" scoped>
.form-error {
  background: rgba(223, 29, 25, 0.15);
}
</style>